import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Download, Trash2, Send, Edit2, Eye, Copy, CheckSquare, Square } from 'lucide-react';
import { independentQuotesService, independentClientsService, independentItinerariesService } from '../../services/independent';

const IndependentQuotesListEnhanced = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [quotesData, clientsData] = await Promise.all([
        independentQuotesService.getAll(),
        independentClientsService.getAll()
      ]);
      setQuotes(quotesData);
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await independentQuotesService.delete(id);
      setQuotes(quotes.filter(q => q.id !== id));
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote');
    }
  };

  const handleSend = async (id) => {
    try {
      await independentQuotesService.update(id, { status: 'sent' });
      setQuotes(quotes.map(q => q.id === id ? { ...q, status: 'sent' } : q));
      alert('Quote marked as sent!');
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote');
    }
  };

  const handleConvertToItinerary = async (quote) => {
    if (!window.confirm(`Convert "${quote.trip_name}" to an itinerary?`)) return;
    try {
      // Converting a quote (rather than manually building an itinerary)
      // creates the itinerary and marks the quote as converted server-side.
      await independentItinerariesService.convertFromQuote(quote.id);

      alert('Quote converted to itinerary successfully!');
      navigate('/independent/itineraries');
      loadData();
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error.response?.data?.error || 'Failed to convert quote to itinerary');
    }
  };

  const handleClone = async (quote) => {
    try {
      const clonedData = {
        ...quote,
        trip_name: `${quote.trip_name} (Copy)`,
        status: 'draft'
      };
      delete clonedData.id;
      delete clonedData.created_at;
      delete clonedData.updated_at;
      
      await independentQuotesService.create(clonedData);
      loadData();
      alert('Quote cloned successfully!');
    } catch (error) {
      console.error('Error cloning quote:', error);
      alert('Failed to clone quote');
    }
  };

  const toggleSelectQuote = (id) => {
    setSelectedQuotes(prev => 
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedQuotes.length === filteredQuotes.length) {
      setSelectedQuotes([]);
    } else {
      setSelectedQuotes(filteredQuotes.map(q => q.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedQuotes.length} selected quotes?`)) return;
    try {
      await Promise.all(selectedQuotes.map(id => independentQuotesService.delete(id)));
      setQuotes(quotes.filter(q => !selectedQuotes.includes(q.id)));
      setSelectedQuotes([]);
      alert('Quotes deleted successfully!');
    } catch (error) {
      console.error('Error deleting quotes:', error);
      alert('Failed to delete some quotes');
    }
  };

  const handleBulkSend = async () => {
    try {
      await Promise.all(selectedQuotes.map(id => 
        independentQuotesService.update(id, { status: 'sent' })
      ));
      setQuotes(quotes.map(q => 
        selectedQuotes.includes(q.id) ? { ...q, status: 'sent' } : q
      ));
      setSelectedQuotes([]);
      alert('Quotes marked as sent!');
    } catch (error) {
      console.error('Error sending quotes:', error);
      alert('Failed to send some quotes');
    }
  };

  const handleExport = () => {
    const csv = [
      ['Quote Number', 'Client', 'Trip Name', 'Destination', 'Start Date', 'End Date', 'Total Price', 'Status'],
      ...filteredQuotes.map(q => [
        q.quote_number || q.id,
        getClientName(q.client_id),
        q.trip_name,
        q.destination,
        q.start_date,
        q.end_date,
        q.total_price,
        q.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quotes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'converted': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter and sort quotes
  let filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.trip_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getClientName(quote.client_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    const matchesDate = (!dateFilter.start || quote.start_date >= dateFilter.start) &&
                       (!dateFilter.end || quote.start_date <= dateFilter.end);
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Sort quotes
  filteredQuotes.sort((a, b) => {
    switch (sortBy) {
      case 'date_desc': return new Date(b.created_at) - new Date(a.created_at);
      case 'date_asc': return new Date(a.created_at) - new Date(b.created_at);
      case 'client': return getClientName(a.client_id).localeCompare(getClientName(b.client_id));
      case 'destination': return (a.destination || '').localeCompare(b.destination || '');
      case 'price_desc': return (b.total_price || 0) - (a.total_price || 0);
      case 'price_asc': return (a.total_price || 0) - (b.total_price || 0);
      default: return 0;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quotes</h1>
          <p className="text-gray-600 mt-1">Manage your travel quotes</p>
        </div>
        <button
          onClick={() => navigate('/independent/quotes/new')}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} className="mr-2" />
          Create Quote
        </button>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="converted">Converted</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="date_desc">Newest First</option>
            <option value="date_asc">Oldest First</option>
            <option value="client">Client Name</option>
            <option value="destination">Destination</option>
            <option value="price_desc">Highest Price</option>
            <option value="price_asc">Lowest Price</option>
          </select>
          <button
            onClick={handleExport}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download size={18} className="mr-2" />
            Export
          </button>
        </div>

        {/* Date Range Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date From</label>
            <input
              type="date"
              value={dateFilter.start}
              onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date To</label>
            <input
              type="date"
              value={dateFilter.end}
              onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedQuotes.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <span className="font-medium text-blue-900">
            {selectedQuotes.length} quote{selectedQuotes.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleBulkSend}
              className="flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Send size={16} className="mr-1" />
              Send All
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
            >
              <Trash2 size={16} className="mr-1" />
              Delete All
            </button>
            <button
              onClick={() => setSelectedQuotes([])}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Quotes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredQuotes.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">No quotes found</p>
          </div>
        ) : (
          filteredQuotes.map((quote) => (
            <div key={quote.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Selection Checkbox */}
                <div className="flex items-start justify-between mb-4">
                  <button
                    onClick={() => toggleSelectQuote(quote.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {selectedQuotes.includes(quote.id) ? (
                      <CheckSquare size={20} className="text-blue-600" />
                    ) : (
                      <Square size={20} className="text-gray-400" />
                    )}
                  </button>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(quote.status)}`}>
                    {quote.status}
                  </span>
                </div>

                {/* Quote Info */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{quote.trip_name}</h3>
                <p className="text-sm text-gray-600 mb-1">📍 {quote.destination}</p>
                <p className="text-sm text-gray-600 mb-1">👤 {getClientName(quote.client_id)}</p>
                {quote.start_date && (
                  <p className="text-sm text-gray-600 mb-3">
                    📅 {new Date(quote.start_date).toLocaleDateString()} - {new Date(quote.end_date).toLocaleDateString()}
                  </p>
                )}
                <p className="text-2xl font-bold text-blue-600 mb-4">
                  ${parseFloat(quote.total_price || 0).toLocaleString()}
                </p>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate(`/independent/quotes/${quote.id}/edit`)}
                    className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleClone(quote)}
                    className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    title="Clone"
                  >
                    <Copy size={16} />
                  </button>
                  {quote.status !== 'converted' && (
                    <button
                      onClick={() => handleConvertToItinerary(quote)}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                      title="Convert to Itinerary"
                    >
                      Convert
                    </button>
                  )}
                  {quote.status === 'draft' && (
                    <button
                      onClick={() => handleSend(quote.id)}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      title="Send"
                    >
                      <Send size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(quote.id)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Select All at Bottom */}
      {filteredQuotes.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={toggleSelectAll}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {selectedQuotes.length === filteredQuotes.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      )}
    </div>
  );
};

export default IndependentQuotesListEnhanced;
